/**
 * ChipInput — tag/chip entry with keyboard support
 *
 * @param {string[]} value   — current tags
 * @param {function} onChange — (tags: string[]) => void
 * @param {string}   placeholder
 * @param {number}   max
 */
import { useState, useRef } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';

export default function ChipInput({
  value = [],
  onChange,
  placeholder = 'Tag ekle...',
  max = 10,
}) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const addTag = (raw) => {
    const tag = raw.trim().toLowerCase();
    if (!tag || value.includes(tag) || value.length >= max) return;
    onChange([...value, tag]);
    setInputValue('');
  };

  const removeTag = (tag) => {
    onChange(value.filter((t) => t !== tag));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    }
    if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  return (
    <div
      className="flex flex-wrap gap-1.5 p-2 border border-gray-200 rounded-xl min-h-[42px] bg-white cursor-text focus-within:ring-2 focus-within:ring-cyan-400 focus-within:border-cyan-400 transition-all"
      onClick={() => inputRef.current?.focus()}
    >
      <AnimatePresence initial={false}>
        {value.map((tag) => (
          <motion.span
            key={tag}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.1 }}
          >
            <Badge
              variant="secondary"
              className="flex items-center gap-1 text-xs bg-cyan-100 text-cyan-800 hover:bg-cyan-200 transition-colors"
            >
              {tag}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
                className="hover:text-rose-500 transition-colors"
              >
                <X size={10} />
              </button>
            </Badge>
          </motion.span>
        ))}
      </AnimatePresence>
      {value.length < max && (
        <input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => addTag(inputValue)}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] outline-none text-sm bg-transparent text-gray-700 placeholder:text-gray-400"
        />
      )}
    </div>
  );
}
